import { MainLayout } from '@/components/layout/MainLayout';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuote, updateQuote, sendQuote, createQuote, type QuoteDetail } from '@/lib/api/quotes';
import { createInvoiceFromQuote } from '@/lib/api/invoices';
import { getJobs } from '@/lib/api/jobs';
import apiClient from '@/lib/api/client';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer, LargeTitleHeader } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PrintableQuote } from '@/components/estimates/PrintableQuote';
import { printQuote } from '@/lib/utils/generateInvoicePdf';
import { getSettings } from '@/lib/api/settings';
import { quotePath } from '@/lib/utils/navPaths';

export const QuoteBuilderPage = () => {
  const { id, clientId, jobId } = useParams<{ id?: string; clientId?: string; jobId?: string }>();
  const navigate = useNavigate();
  const { toasts, removeToast, toast } = useToast();
  const printableRef = useRef<HTMLDivElement>(null);
  const [quoteData, setQuoteData] = useState<QuoteDetail | null>(null);
  const [, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);
  const [jobs, setJobs] = useState<Array<{ id: number; jobNumber: string; title: string; client: string; clientId?: number }>>([]);
  const [sections, setSections] = useState<Array<{ id: string; title: string; items: Array<{ id: string; name: string; description: string; quantity: number; unitPrice: number; unit: string }> }>>([]);
  const [taxRate, setTaxRate] = useState(5); // BC: GST only, matches invoicing
  const [confirmSendQuote, setConfirmSendQuote] = useState(false);
  const [confirmDeleteSectionId, setConfirmDeleteSectionId] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  } | undefined>(undefined);

  // Keyboard shortcut: Ctrl+S → save, Ctrl+P → print
  const handleSaveDraftRef = useRef<() => void>();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSaveDraftRef.current?.(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') { e.preventDefault(); if (printableRef.current) printQuote(printableRef.current); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Fetch company settings for printing
  useEffect(() => {
    getSettings()
      .then((settings) => {
        if (settings.company) {
          const c = settings.company;
          const addressParts = [
            c.address,
            c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state,
            c.zipCode,
          ].filter(Boolean);

          setCompanyInfo({
            name: c.companyName || 'OchrePoint Contracting',
            address: addressParts.join('\n'),
            phone: c.phone || '',
            email: c.email || '',
            logo: c.logoUrl,
          });
        }
      })
      .catch((err) => {
        console.error('Failed to load company settings:', err);
      });
  }, []);

  useEffect(() => {
    if (id) {
      loadQuote();
    } else {
      // New quote - show job selector
      loadJobs();
      setShowJobSelector(true);
    }
  }, [id]);

  // Upgrade the flat /quotes/:id URL to the canonical /clients/:c/jobs/:j/quotes/:q once
  // the quote (and thus its client/job) is known, so the address bar mirrors the hierarchy.
  useEffect(() => {
    if (!id || !quoteData?.client_id || !quoteData.job_id) return;
    if (String(clientId) !== String(quoteData.client_id) || String(jobId) !== String(quoteData.job_id)) {
      navigate(quotePath(quoteData.client_id, quoteData.job_id, id), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteData, clientId, jobId, id]);

  const loadJobs = async () => {
    try {
      const response = await getJobs();
      // Extract jobs array from response
      setJobs(Array.isArray(response.jobs) ? response.jobs : []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      setJobs([]);
    }
  };

  const handleSelectJob = async (jobId: number) => {
    try {
      setSaving(true);
      const newQuote = await createQuote(jobId.toString());
      setShowJobSelector(false);
      const job = jobs.find(j => j.id === jobId);
      navigate(quotePath(job?.clientId, jobId, newQuote.id));
    } catch (error) {
      console.error('Failed to create quote:', error);
      toast.error('Failed to create quote');
    } finally {
      setSaving(false);
    }
  };

  const saveSectionsToBackend = async () => {
    if (!id) return;
    const sectionsData = sections.map((section, si) => ({
      id: section.id.startsWith('temp-') ? undefined : parseInt(section.id),
      name: section.title,
      display_order: si,
      items: section.items.map((item, ii) => ({
        id: item.id.startsWith('temp-') ? undefined : parseInt(item.id),
        item_name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        unit: item.unit,
        display_order: ii,
      })),
    }));
    await apiClient.put(`/quotes/${id}/sections`, { sections: sectionsData, tax_rate: taxRate });
  };

  const loadQuote = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await getQuote(id);
      setQuoteData(data);
      
      // Transform sections and items from API
      if (data.sections && data.sections.length > 0) {
        const transformedSections = data.sections.map((section: any) => ({
          id: section.id.toString(),
          title: section.name || section.title,
          items: data.items
            .filter((item: any) => item.section_id === section.id.toString() || item.section_id === section.id)
            .map((item: any) => ({
              id: item.id.toString(),
              name: item.item_name || item.name,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unit_price,
              unit: item.unit || 'ea',
            })),
        }));
        setSections(transformedSections);
      }
      
      // Set tax rate if available
      if (data.tax_rate != null) { // 0 is a real rate, not "unset"
        setTaxRate(Number(data.tax_rate));
      }
    } catch (error) {
      console.error('Failed to load quote:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = useCallback(async () => {
    if (!id) return;
    try {
      setSaving(true);
      // inline save to avoid stale closure
      const sectionsData = sections.map((section, si) => ({
        id: section.id.startsWith('temp-') ? undefined : parseInt(section.id),
        name: section.title,
        display_order: si,
        items: section.items.map((item, ii) => ({
          id: item.id.startsWith('temp-') ? undefined : parseInt(item.id),
          item_name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          unit: item.unit,
          display_order: ii,
        })),
      }));
      await apiClient.put(`/quotes/${id}/sections`, { sections: sectionsData, tax_rate: taxRate });
      await updateQuote(id, { status: 'draft' });
      toast.success('Quote saved');
      loadQuote();
    } catch (error) {
      console.error('Failed to save quote:', error);
      toast.error('Failed to save quote');
    } finally {
      setSaving(false);
    }
  }, [id, sections]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keep ref in sync for keyboard shortcut
  useEffect(() => { handleSaveDraftRef.current = handleSaveDraft; }, [handleSaveDraft]);

  // Clone/duplicate this quote into a new one
  const handleCloneQuote = async () => {
    if (!id || !quoteData) return;
    setCloning(true);
    try {
      await saveSectionsToBackend();
      // Create a new quote for the same job, then copy sections
      const newQuote = await createQuote(quoteData.job_id?.toString() || '');
      const sectionsData = sections.map((section, si) => ({
        name: section.title,
        display_order: si,
        items: section.items.map((item, ii) => ({
          item_name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          unit: item.unit,
          display_order: ii,
        })),
      }));
      await apiClient.put(`/quotes/${newQuote.id}/sections`, { sections: sectionsData, tax_rate: taxRate });
      await updateQuote(String(newQuote.id), { status: 'draft' });
      toast.success(`Quote cloned as ${newQuote.quoteNumber || `#${newQuote.id}`}`);
      navigate(quotePath(quoteData.client_id, quoteData.job_id, newQuote.id));
    } catch {
      toast.error('Failed to clone quote');
    } finally {
      setCloning(false);
    }
  };

  const handleSendQuote = () => {
    if (!id) return;
    setConfirmSendQuote(true);
  };

  const confirmSendQuoteAction = async () => {
    if (!id) return;
    setConfirmSendQuote(false);
    try {
      setSaving(true);
      await saveSectionsToBackend();
      const emailResult = await sendQuote(id);
      await updateQuote(id, { status: 'sent' });
      const receipt = emailResult.emailed && emailResult.emailTo
        ? `Quote emailed to ${emailResult.emailTo} · Copy saved to Sent folder`
        : emailResult.emailed
        ? 'Quote emailed to client · Copy saved to Sent folder'
        : 'Quote marked as sent (email not delivered)';
      toast.success(receipt);
      navigate('/quotes');
    } catch (error) {
      console.error('Failed to send quote:', error);
      toast.error('Failed to send quote');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!printableRef.current) return;
    printQuote(printableRef.current);
  };

  const handleConvertToInvoice = async () => {
    if (!id || !quoteData) return;
    try {
      setSaving(true);
      const result = await createInvoiceFromQuote(parseInt(id));
      toast.success(`Invoice ${result.invoiceNumber} created successfully!`);
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to convert quote to invoice:', error);
      toast.error('Failed to create invoice from quote');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = () => {
    if (!newSectionName.trim()) return;
    setSections([...sections, { id: `temp-${Date.now()}`, title: newSectionName.trim(), items: [] }]);
    setNewSectionName('');
    setShowAddSection(false);
  };

  const handleDeleteSection = (sectionId: string) => {
    setConfirmDeleteSectionId(sectionId);
  };

  const confirmDeleteSectionAction = () => {
    if (!confirmDeleteSectionId) return;
    setSections(sections.filter((s) => s.id !== confirmDeleteSectionId));
    setConfirmDeleteSectionId(null);
  };

  const handleAddLineItem = (sectionId: string) => {
    const newItem = {
      id: `temp-item-${Date.now()}`,
      name: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      unit: 'ea',
    };

    setSections(sections.map((section) =>
      section.id === sectionId
        ? { ...section, items: [...section.items, newItem] }
        : section
    ));
  };

  const handleDeleteLineItem = (sectionId: string, itemId: string) => {
    setSections(sections.map((section) =>
      section.id === sectionId
        ? { ...section, items: section.items.filter((item) => item.id !== itemId) }
        : section
    ));
  };

  const handleUpdateLineItem = (sectionId: string, itemId: string, field: string, value: string | number) => {
    setSections(sections.map((section) =>
      section.id === sectionId
        ? {
            ...section,
            items: section.items.map((item) =>
              item.id === itemId ? { ...item, [field]: value } : item
            ),
          }
        : section
    ));
  };

  const calculateSubtotal = () => {
    return sections.reduce((total, section) => {
      const sectionTotal = section.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      return total + sectionTotal;
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const tax = (subtotal * taxRate) / 100;
  const grandTotal = subtotal + tax;

  return (
    <MainLayout>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {/* Hidden Printable Area */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, backgroundColor: '#ffffff' }}>
        {quoteData && (
          <PrintableQuote 
            ref={printableRef} 
            quote={quoteData} 
            companyInfo={companyInfo} 
          />
        )}
      </div>

      {/* Job Selector Modal */}
      {showJobSelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Select Job for Quote
            </h2>
            <div className="space-y-2">
              {jobs.map((job) => (
                <button
                  key={job.id}
                  onClick={() => handleSelectJob(job.id)}
                  disabled={saving}
                  className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-primary/10 transition-colors disabled:opacity-50"
                >
                  <div className="font-bold text-gray-900">
                    {job.jobNumber} - {job.title}
                  </div>
                  <div className="text-sm text-gray-600">
                    {job.client}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => navigate('/quotes')}
              className="mt-4 w-full py-2 px-4 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 flex flex-col gap-6">
          {/* iOS Large Title Header */}
          <LargeTitleHeader
            title="Quote Builder"
            subtitle={quoteData?.quote_number || 'New Quote'}
            onBack={() => {
              const jid = jobId || quoteData?.job_id;
              if (clientId && jid) navigate(`/clients/${clientId}/jobs/${jid}`);
              else navigate('/quotes');
            }}
            backLabel="Back"
            actions={
              <div className="flex items-center gap-2">
                {quoteData?.status === 'approved' && (
                  <button
                    onClick={handleConvertToInvoice}
                    disabled={saving || !id}
                    className="flex items-center gap-1.5 h-9 px-3 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">receipt_long</span>
                    <span className="hidden sm:inline">Invoice</span>
                  </button>
                )}
                <button
                  onClick={handleCloneQuote}
                  disabled={cloning || !id}
                  title="Clone this quote"
                  className="flex items-center gap-1.5 h-9 px-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-bold hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">content_copy</span>
                  <span className="hidden sm:inline">Clone</span>
                </button>
                <button
                  onClick={handleSaveDraft}
                  disabled={saving || !id}
                  title="Save (Ctrl+S)"
                  className="flex items-center gap-1.5 h-9 px-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-bold hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={handlePrint}
                  disabled={!id || !quoteData}
                  className="hidden sm:flex items-center gap-1.5 h-9 px-3 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-bold hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">print</span>
                  Print
                </button>
                <button
                  onClick={handleSendQuote}
                  disabled={saving || !id}
                  className="flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">send</span>
                  <span className="hidden sm:inline">{saving ? 'Sending…' : 'Send'}</span>
                </button>
              </div>
            }
          />

          {/* Quote Info */}
          <div className="rounded-xl bg-white border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Quote Number
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={quoteData?.quote_number || 'Loading...'}
                    disabled
                    className="form-input w-full rounded-lg border-gray-200 bg-gray-50 text-gray-900 px-4 py-2 text-sm disabled:opacity-50"
                  />
                  {quoteData?.quote_number && (
                    <button
                      type="button"
                      title="Copy quote number"
                      onClick={() => { navigator.clipboard?.writeText(quoteData.quote_number); toast.success('Copied!'); }}
                      className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors"
                    >
                      <span className="material-symbols-outlined text-base">content_copy</span>
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Job/Client
                </label>
                <input
                  type="text"
                  value={quoteData?.job_title ? `${quoteData.job_title} - ${quoteData.client_name}` : 'Loading...'}
                  disabled
                  className="form-input w-full rounded-lg border-gray-200 bg-gray-50 text-gray-900 px-4 py-2 text-sm disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Status
                </label>
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium capitalize ${
                  quoteData?.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                  quoteData?.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                  quoteData?.status === 'approved' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {quoteData?.status || 'Loading...'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Created
                </label>
                <input
                  type="text"
                  value={quoteData?.created_at ? new Date(quoteData.created_at).toLocaleDateString() : 'Loading...'}
                  disabled
                  className="form-input w-full rounded-lg border-gray-200 bg-gray-50 text-gray-900 px-4 py-2 text-sm disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          {sections.map((section) => {
            const sectionSubtotal = section.items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
            return (
            <div
              key={section.id}
              className="rounded-xl bg-white border border-gray-200 overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">
                  {section.title}
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-primary">${sectionSubtotal.toFixed(2)}</span>
                  <button
                    onClick={() => handleDeleteSection(section.id)}
                    className="text-gray-600 hover:text-error"
                    title="Delete section"
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                        Item
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                        Unit Price
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                        Total
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {section.items.map((item, itemIdx) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            autoFocus={itemIdx === section.items.length - 1 && itemIdx > 0}
                            value={item.name}
                            onChange={(e) => handleUpdateLineItem(section.id, item.id, 'name', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && itemIdx === section.items.length - 1) {
                                e.preventDefault();
                                handleAddLineItem(section.id);
                              }
                            }}
                            placeholder="Item name"
                            className="w-full px-2 py-1 text-sm rounded border border-gray-200 bg-transparent text-gray-900"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleUpdateLineItem(section.id, item.id, 'description', e.target.value)}
                            placeholder="Description"
                            className="w-full px-2 py-1 text-sm rounded border border-gray-200 bg-transparent text-gray-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleUpdateLineItem(section.id, item.id, 'quantity', parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1 text-sm text-right rounded border border-gray-200 bg-transparent text-gray-900"
                            />
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => handleUpdateLineItem(section.id, item.id, 'unit', e.target.value)}
                              placeholder="ea"
                              className="w-12 px-2 py-1 text-sm rounded border border-gray-200 bg-transparent text-gray-900"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => handleUpdateLineItem(section.id, item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="w-24 px-2 py-1 text-sm text-right rounded border border-gray-200 bg-transparent text-gray-900"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                          ${(item.quantity * item.unitPrice).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button 
                            onClick={() => handleDeleteLineItem(section.id, item.id)}
                            className="text-gray-600 hover:text-error"
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => handleAddLineItem(section.id)}
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                  Add Item (or press Enter on last row)
                </button>
              </div>
            </div>
            );
          })}

          {/* Add Section */}
          {showAddSection ? (
            <div className="flex gap-2 items-center rounded-xl border-2 border-dashed border-primary/40 p-4">
              <input
                type="text"
                autoFocus
                value={newSectionName}
                onChange={e => setNewSectionName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddSection(); if (e.key === 'Escape') { setShowAddSection(false); setNewSectionName(''); } }}
                placeholder="Section name (e.g. Framing, Finishes)"
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button onClick={handleAddSection} className="px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90">Add</button>
              <button onClick={() => { setShowAddSection(false); setNewSectionName(''); }} className="px-3 py-2 text-gray-500 text-sm rounded-lg hover:bg-gray-100">Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSection(true)}
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 p-6 text-gray-600 hover:border-primary hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined">add</span>
              Add Section
            </button>
          )}
        </div>

        {/* Financial Summary Sidebar */}
        <div className="w-80 flex-shrink-0">
          <div className="sticky top-6 rounded-xl bg-white border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Financial Summary
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Subtotal
                </span>
                <span className="font-medium text-gray-900">
                  ${subtotal.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Tax ({taxRate}%)
                </span>
                <span className="font-medium text-gray-900">
                  ${tax.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-lg font-bold text-gray-900">
                    Grand Total
                  </span>
                  <span className="text-2xl font-black text-primary">
                    ${grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Tax Rate (%)
              </label>
              <input
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(Number(e.target.value))}
                className="form-input w-full rounded-lg border-gray-200 bg-gray-50 text-gray-900 px-4 py-2 text-sm"
                step="0.1"
              />
            </div>
          </div>
        </div>
      </div>

      {confirmSendQuote && (
        <ConfirmDialog
          title="Send Quote"
          message="Send this quote to the client?"
          confirmLabel="Send"
          variant="default"
          onConfirm={confirmSendQuoteAction}
          onCancel={() => setConfirmSendQuote(false)}
        />
      )}

      {confirmDeleteSectionId && (
        <ConfirmDialog
          title="Delete Section"
          message="Delete this section and all its items?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteSectionAction}
          onCancel={() => setConfirmDeleteSectionId(null)}
        />
      )}
    </MainLayout>
  );
};


